/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { StyledPrimarySidebarItem, disabledItemStyle } from "./styles";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import '../../styles.css'
import { colors } from "../../themes/dashboard/index"

export default function PrimarySidebar({
  categories,
  activeTab,
  setActiveTab,
  isCollapsed,
  expandedItems,
  toggleExpandedItem,
  onReportsClickInCollapsedState,
  isMobile,
  setMobileOpen
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e, item) => {
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return;
    }
    e.preventDefault();

    // Don't navigate if item is disabled or no path
    if (item.disabled || !item.path) {
      return;
    }

    setActiveTab(item.id);
    navigate(item.path);
    if(isMobile) {
      setMobileOpen(false)
    }
  }

  const handleExpandableClick = (e, item) => {
    e.preventDefault();

    if (isCollapsed && item.id === "reports") {
      onReportsClickInCollapsedState();
      return;
    }

    if (item.type === "expandable" || item.type === "expandable-section") {
      toggleExpandedItem(item.id);
    }
    if (item.path && !item.disabled) {
      setActiveTab(item.id);
      navigate(item.path);
    }
  }

  const handleChildClick = (e, childItem, parentId) => {
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return;
    }
    e.preventDefault();

    if (childItem.path && !childItem.permission) {
      setActiveTab(parentId);
      navigate(childItem.path);
    }

    if(isMobile) {
      setMobileOpen(false)
    }
  }

  const isItemActive = (item) => {
    if (item.type === "expandable" && item.children) {
      // Check if any child is active
      return item?.children?.some(section =>
        section?.children?.some(child =>
          location.pathname === child.path
        )
      );
    }
    return location.pathname === item.path;
  }

  const isChildActive = (childItem) => {
    if (!childItem.path) {
      return false;
    }

    return location.pathname === childItem.path || location.pathname.startsWith(`${childItem.path}/`);
  }

  const renderTreeItem = (item, level = 0, parentId = null) => {
    const isActive = isItemActive(item);
    const isExpanded = expandedItems.has(item.id);

    if (item.type === "simple") {
      return (
        <StyledPrimarySidebarItem
          key={item.id}
          href={item.path}
          className={item.disabled ? `nav-item-disabled tree-item level-${level} ${activeTab === item.id ? "active" : ""}` : `nav-item tree-item level-${level} ${activeTab === item.id ? "active" : ""}`}
          onClick={(e) => handleClick(e, item)}
          style={item.disabled ? disabledItemStyle : {}}
        >
          <span className="nav-icon">{item.icon}</span>
          {!isCollapsed && (
            <span className="nav-label">{item.label}</span>
          )}
        </StyledPrimarySidebarItem>
      );
    }

    if (item.type === "expandable") {
      return (
        <div key={item.id} className={`tree-expandable-item level-${level}`}>
          <StyledPrimarySidebarItem
            href={item.path}
            className={item.disabled ? `nav-item-disabled tree-expandable level-${level} ${activeTab === item.id ? "active" : ""}` : `nav-item tree-expandable level-${level} ${activeTab === item.id ? "active" : ""}`}
            onClick={(e) => handleExpandableClick(e, item)}
            style={item.disabled ? disabledItemStyle : {}}
          >
            <span className="nav-icon">{item.icon}</span>
            {!isCollapsed && (
              <React.Fragment>
                <span className="nav-label">{item.label}</span>
                <button className="expand-toggle">
                  {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </React.Fragment>
            )}
          </StyledPrimarySidebarItem>

          {!isCollapsed && isExpanded && item.children && (
            <div className="tree-children">
              {item.children.map((section) => renderTreeItem(section, level + 1, item.id))}
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'non-expandable-section') {
      const isActive = isChildActive(item)
      return (
        <StyledPrimarySidebarItem
          key={item.id}
          href={item.path}
          className={!item?.permission ? `non-expandable-section-item` : 'disabled-non-expandable-section-item'}
          style={{background: isActive && '#FFF7ED', fontWeight: isActive && '500', marginBottom: '10px'}}
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return;
            }
            e.preventDefault();
            navigate(item.path);
            if (isMobile) {
              setMobileOpen(false);
            }
          }}
        >
          <span style={{color: !item?.permission ? isActive ? colors.deep : '' : '#a6a6a6'}}>{item?.label}</span>
        </StyledPrimarySidebarItem>
      );
    }

    if (item.type === "expandable-section") {
      const isSectionExpanded = expandedItems.has(item.id);
      
      return (
        <div key={item.id} className={`tree-expandable-section level-${level}`}>
          {/* Section header with toggle */}
          <div 
            className={`tree-section-header level-${level} ${isSectionExpanded ? "expanded" : ""}`}
            onClick={() => toggleExpandedItem(item.id)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span className="section-label">{item.label}</span>
            <button 
              className="expand-toggle"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpandedItem(item.id);
              }}
            >
              {isSectionExpanded ? <FiChevronUp /> : <FiChevronDown />}
            </button>
          </div>
          
          {/* Section children */}
          {isSectionExpanded && item.children && (
            <div className="tree-section-children">
              {item.children.map((child) => (
                <div key={child.id} className="tree-child-item level-2">
                  <StyledPrimarySidebarItem
                    href={child.path}
                    className={`tree-child-nav ${isChildActive(child) ? "active" : ""}`}
                    onClick={(e) => handleChildClick(e, child, parentId)}
                    style={{
                      pointerEvents: child?.permission ? 'none' : 'auto',
                    }}
                  >
                    <span 
                      className={`child-label ${isChildActive(child) ? "active" : ""}`}
                      style={{ 
                        color: child?.permission && '#a6a6a6'
                      }}
                    >
                      {child.label}
                    </span>
                  </StyledPrimarySidebarItem>
                </div>
              ))}
            </div>
          )}

        </div>
      );
    }

    return null;
  };

  return (
    <div className={`primary-sidebar ${isCollapsed ? "collapsed" : ""}`}>
      <div className="sidebar-header"></div>

      <nav className="sidebar-nav">
        {categories.map((category) => (
          <div 
            key={category.id} 
            className="sidebar-category"
            style={{
              marginBottom: !isCollapsed && '20px'
            }}
          >
            {!isCollapsed && (
              <div className="category-label">
                {category.label}
              </div>
            )}
            <div className="category-items">
              {category.items.map((item) => renderTreeItem(item, 0))}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );
}
